import moment from 'moment-timezone';
import { Report } from '../models/Report';
import { I18n } from '../wrappers/I18n';
import { SERVER_PDF_ERROR, serverPdf } from './ServerPdfService';
import { savePdfFile } from './pdfFile';
const DATE_FORMAT = 'DD.MM.YYYY';
/**
 * Reports as server PDFs: which reports are offered, how each is named and
 * laid out, and the save / email actions the report screens call.
 */
class ReportPdf {
    /**
     * Offered while the server renders reports, for every report type except
     * the legacy ones — those reports come off the fiscal printer and the app
     * only draws their frame.
     */
    isOffered(report) {
        if (!report?.type || !serverPdf.supports('report'))
            return false;
        return this.reportType(report)?.processMode !== 'legacy';
    }
    document(report) {
        const reportType = this.reportType(report);
        const title = reportType?.name || I18n.t('reports.' + report.type);
        const period = this.period(report);
        return {
            kind: 'report',
            // A report type that declares a width (the bar ledger's wide table) prints landscape.
            profile: reportType?.width ? 'report_wide' : 'report',
            title,
            period,
            fileName: `${title} ${period || moment().format(DATE_FORMAT)}`,
            pageLabel: I18n.t('pdf.page_label'),
            lang: this.language(),
            wrapperClass: 'report',
        };
    }
    /** A year report by its year; a range by its dates, one date when it covers a single day; none without a range. */
    period(report) {
        if (report.year)
            return String(report.year);
        if (!report.startTime || !report.endTime)
            return '';
        const from = report.startTime.format(DATE_FORMAT);
        const to = report.endTime.format(DATE_FORMAT);
        return from === to ? from : `${from}–${to}`;
    }
    /** Renders on the server and hands the file to the user (download / share sheet). */
    async save(report, endpointBase = '') {
        const printable = await this.printable(report);
        if (!printable)
            return { success: false, error: SERVER_PDF_ERROR.general };
        const document = this.document(report);
        const result = await serverPdf.render(printable, document, endpointBase);
        if (!result.success)
            return result;
        if (!result.pdf)
            return { success: false, error: SERVER_PDF_ERROR.general };
        return await savePdfFile(result.pdf, document.fileName)
            ? result
            : { success: false, error: SERVER_PDF_ERROR.saveFailed };
    }
    async email(report, to, endpointBase = '') {
        const printable = await this.printable(report);
        if (!printable)
            return { success: false, error: SERVER_PDF_ERROR.general };
        return serverPdf.email(printable, this.document(report), to, endpointBase);
    }
    /** The same printable the screen shows; a failure becomes a result the screens can toast. */
    async printable(report) {
        try {
            return await report.getPrintable();
        }
        catch (error) {
            console.error('Report PDF: building the report failed', error);
            return null;
        }
    }
    reportType(report) {
        return Report.types().find(type => type.type === report.type);
    }
    language() {
        return String(I18n.$i18n?.locale?.value || 'sr');
    }
}
export const reportPdf = new ReportPdf();
//# sourceMappingURL=reportPdf.js.map